/*
  File: src/components/Stream/RightSideMenu.jsx
  Purpose: Slide-in right menu inside the player with tabs: Match Stats, Events, Betting.
  Notes: Uses framer-motion for smooth animation. Dummy data until APIs are provided.
*/
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFutbol, faSquare, faRightLeft, faVideo, faFlag, faShirt, faArrowDown, faArrowUp, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import bettingProviders from '../../config/bettingProviders';
import { useAuth } from '../../auth/AuthContext';

const StatsIcon = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path opacity=".6" d="M4 4h16v2H4z" />
    <path d="M6 10h3v10H6zM11 7h3v13h-3zM16 13h3v7h-3z" />
  </svg>
);
const EventsIcon = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M7 10h10v2H7zM7 14h7v2H7z" />
    <path opacity=".7" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2Zm0 14H5V9h14v9Z" />
  </svg>
);
const BetIcon = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M12 2l2.39 7.36H22l-6.19 4.5L18.2 21 12 16.9 5.8 21l2.39-7.14L2 9.36h7.61L12 2z" />
  </svg>
);
const ChatIcon = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
  </svg>
);
// Custom penalty icon: goal net with a ball
const PenaltyIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Goal frame */}
    <rect x="3" y="6" width="18" height="10" rx="1.5" ry="1.5" />
    {/* Net lines vertical */}
    <path d="M7 6v10M11 6v10M15 6v10M19 6v10" opacity="0.7" />
    {/* Net lines horizontal */}
    <path d="M3 9h18M3 12h18M3 15h18" opacity="0.7" />
    {/* Ball */}
    <circle cx="14.5" cy="13" r="2.2" fill="currentColor" stroke="none" />
  </svg>
);

export default function RightSideMenu({ open, onClose, isFullscreen, matchId = null, onWidthChange = null, onOpenWebview = null }) {
  const { user } = useAuth();
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('auth:user') || 'null'); } catch { return null; }
  }, []);
  const effectiveUser = user || localUser;
  const [tab, setTab] = useState('stats'); // stats | events | betting | lineup
  const [teamStats, setTeamStats] = useState(null); // { a, b }
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [matchMeta, setMatchMeta] = useState(null); // the match object from /matches
  const [teamColors, setTeamColors] = useState({ left: '#22d3ee', right: '#818cf8' });
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all'); // 'all' or EVENT_TYPE
  const [eventTeamFilter, setEventTeamFilter] = useState('all'); // 'all' or team slug
  const [lineups, setLineups] = useState(null); // { home: {formation, starters: [...]}, away: {...} }
  const [lineupsLoading, setLineupsLoading] = useState(false);
  const [lineupsError, setLineupsError] = useState('');
  const [betPick, setBetPick] = useState(null); // { market, selection, odds }
  const [showBetProviders, setShowBetProviders] = useState(false);

  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [userTeamPreference, setUserTeamPreference] = useState(null); // 'home', 'away', or 'neutral'
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const chatContainerRef = useRef(null);
  const simulationIntervalRef = useRef(null);


  // Track URL changes to detect match switching
  const [currentUrl, setCurrentUrl] = React.useState(window.location.href);
  React.useEffect(() => {
    const handleUrlChange = () => setCurrentUrl(window.location.href);
    window.addEventListener('popstate', handleUrlChange);
    // Also watch for programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handleUrlChange();
    };
    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handleUrlChange();
    };
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const effectiveMatchId = React.useMemo(() => {
    if (matchId) return matchId;
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const q = sp.get('match_id');
      if (q) {
        const matchIdFromUrl = Number(q);
        // Update sessionStorage with current match_id from URL
        try {
          sessionStorage.setItem('current_match_id', String(matchIdFromUrl));
        } catch { }
        return matchIdFromUrl;
      }
    } catch { }
    try {
      const sid = sessionStorage.getItem('current_match_id') || sessionStorage.getItem('match_id');
      if (sid) return Number(sid);
    } catch { }
    return null;
  }, [matchId, currentUrl]);

  // Clear stale state when match changes
  React.useEffect(() => {
    setTeamStats(null);
    setMatchMeta(null);
    setEvents([]);
    setLineups(null);
    setStatsError('');
    setEventsError('');
    setLineupsError('');
    setEventTypeFilter('all');
    setEventTeamFilter('all');
    setChatMessages([]);
  }, [effectiveMatchId]);

  // Always keep chat scrolled to bottom when new messages arrive (if chat tab open)
  useEffect(() => {
    if (tab === 'chat' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, tab]);

  // Ensure simulation is running whenever chat tab is active; stop when not
  useEffect(() => {
    if (tab === 'chat') {
      if (chatMessages.length === 0) {
        generateInitialChatMessages();
      }
      startChatSimulation();
    } else {
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
      }
    }
    return () => {
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
      }
    };
  }, [tab]);

  // Team data mapped by team_id (PSG=1, Bayern=2, Inter=3)
  const teamDataById = {
    1: { // PSG
      id: 1,
      slug: 'psg',
      name: 'PSG',
      fullName: 'Paris Saint-Germain',
      hashtags: ['#PSG', '#AllezParis', '#ICICESTPARIS'],
      chants: ['Allez Paris!', 'Ici c\'est Paris!', 'Paris Saint-Germain!', 'Allez les Rouges et Bleus!'],
      fanNames: ['Pierre', 'Jean', 'Marie', 'Sophie', 'Antoine', 'Camille', 'Luc', 'Isabelle', 'ParisFan', 'PSG4Life']
    },
    2: { // Bayern
      id: 2,
      slug: 'bayern',
      name: 'Bayern',
      fullName: 'Bayern Munich',
      hashtags: ['#FCB', '#MiaSanMia', '#Bayern'],
      chants: ['Mia san mia!', 'FC Bayern forever!', 'Stern des Südens!', 'Bayern Munich!'],
      fanNames: ['Hans', 'Klaus', 'Thomas', 'Anna', 'Michael', 'Stefan', 'Julia', 'Franz', 'BayernFan', 'MiaSanMia']
    },
    3: { // Inter
      id: 3,
      slug: 'inter',
      name: 'Inter',
      fullName: 'Inter Milan',
      hashtags: ['#Inter', '#ForzaInter', '#FCIM'],
      chants: ['Forza Inter!', 'Pazza Inter Amala!', 'C\'è solo l\'Inter!', 'Inter campione!'],
      fanNames: ['Marco', 'Giuseppe', 'Luca', 'Francesca', 'Alessandro', 'Giulia', 'Roberto', 'Valentina', 'InterFan', 'Nerazzurri']
    }
  };

  // Detect current match teams from teamStats
  const currentTeams = React.useMemo(() => {
    if (!teamStats) return { home: null, away: null };
    const homeId = teamStats.a?.team_id;
    const awayId = teamStats.b?.team_id;
    return {
      home: teamDataById[homeId] || null,
      away: teamDataById[awayId] || null,
      homeId,
      awayId
    };
  }, [teamStats]);

  // Generate initial chat messages based on current match teams (50% fans, 50% neutral)
  const generateInitialChatMessages = () => {
    const initialMessages = [];
    const messageCount = 20 + Math.floor(Math.random() * 15);

    // Build team fan bases from detected teams
    const teamFanBases = [];
    if (currentTeams.home) {
      teamFanBases.push({ 
        team: currentTeams.home.slug, 
        teamData: currentTeams.home,
        names: currentTeams.home.fanNames 
      });
    }
    if (currentTeams.away) {
      teamFanBases.push({ 
        team: currentTeams.away.slug, 
        teamData: currentTeams.away,
        names: currentTeams.away.fanNames 
      });
    }
    
    // Neutral fan base
    const neutralFanBase = { 
      team: 'neutral', 
      teamData: null,
      names: ['Alex', 'Chris', 'Sam', 'Taylor', 'Jordan', 'Casey', 'Morgan', 'Dylan'] 
    };

    // Generate dynamic match events based on teams
    const homeName = currentTeams.home?.name || 'Home';
    const awayName = currentTeams.away?.name || 'Away';
    const matchEvents = [
      { minute: '12', event: `Early pressure from ${homeName}` },
      { minute: '23', event: `${awayName} counter attack` },
      { minute: '34', event: 'Great save by keeper' },
      { minute: '41', event: `${homeName} free kick opportunity` },
      { minute: '55', event: `${awayName} substitution` },
      { minute: '67', event: `${homeName} corner kick` },
      { minute: '74', event: `${awayName} yellow card` },
      { minute: '82', event: `${homeName} attacking` }
    ];

    // Generate messages with 50% team fans, 50% neutral
    for (let i = 0; i < messageCount; i++) {
      let fanBase;
      let message = '';
      
      // 50/50 split: even indices = team fans, odd indices = neutral
      const isTeamFan = i % 2 === 0;
      
      if (isTeamFan && teamFanBases.length > 0) {
        // Pick random team fan
        fanBase = teamFanBases[Math.floor(Math.random() * teamFanBases.length)];
        const username = `${fanBase.names[Math.floor(Math.random() * fanBase.names.length)]}${Math.floor(Math.random() * 99)}`;
        
        // Generate team-specific messages
        const td = fanBase.teamData;
        const messages = [
          `${td.chants[0]} ${td.hashtags[Math.floor(Math.random() * td.hashtags.length)]}`,
          `${td.name} is unstoppable tonight! ${td.hashtags[0]}`,
          `${td.chants[1]} Let's go!`,
          `What a run by ${td.name}!`,
          `${td.name} keeper is a wall! ${td.hashtags[1]}`,
          `We need to score before halftime!`,
          `${td.name} is on fire!`,
          `${td.fullName} will win this! ${td.hashtags[2] || td.hashtags[0]}`,
          `${td.chants[2] || td.chants[0]}`,
          `${td.name} magic incoming!`
        ];
        message = messages[Math.floor(Math.random() * messages.length)];
        
        // Add some match event context (30% chance)
        if (Math.random() < 0.3 && i > 5) {
          const event = matchEvents[Math.floor(Math.random() * matchEvents.length)];
          if (Math.random() < 0.5) {
            message = `${event.minute}' - ${event.event}. ${message}`;
          }
        }
        
        initialMessages.push({
          id: `msg-${i}`,
          username,
          message,
          timestamp: Date.now() - (messageCount - i) * 30000,
          isOwn: false,
          team: fanBase.team
        });
      } else {
        // Neutral fan message
        fanBase = neutralFanBase;
        const username = `${fanBase.names[Math.floor(Math.random() * fanBase.names.length)]}${Math.floor(Math.random() * 99)}`;
        
        const messages = [
          `What a match! Two European giants`,
          `The quality on display is incredible`,
          `Hope this goes to extra time!`,
          `Both teams playing beautiful football`,
          `This is Champions League at its best`,
          `What a tactical battle`,
          `The intensity is unbelievable`,
          `World class teams everywhere`,
          `This is why I love football`,
          `What a spectacle!`
        ];
        message = messages[Math.floor(Math.random() * messages.length)];
        
        initialMessages.push({
          id: `msg-${i}`,
          username,
          message,
          timestamp: Date.now() - (messageCount - i) * 30000,
          isOwn: false,
          team: fanBase.team
        });
      }
    }

    setChatMessages(initialMessages.sort((a, b) => a.timestamp - b.timestamp));
  };

  // Simulate incoming chat messages (every ~3s, no pauses) with 50/50 split
  const startChatSimulation = () => {
    if (simulationIntervalRef.current) {
      clearInterval(simulationIntervalRef.current);
    }

    let messageCounter = 0; // Track messages for 50/50 split

    simulationIntervalRef.current = setInterval(() => {
      if (tab !== 'chat') {
        clearInterval(simulationIntervalRef.current);
        return;
      }

      // Build dynamic team fan bases from current teams
      const teamFanBases = [];
      if (currentTeams.home) {
        teamFanBases.push({ 
          team: currentTeams.home.slug, 
          teamData: currentTeams.home,
          names: currentTeams.home.fanNames.slice(8) // Use second half of names for variety
        });
      }
      if (currentTeams.away) {
        teamFanBases.push({ 
          team: currentTeams.away.slug, 
          teamData: currentTeams.away,
          names: currentTeams.away.fanNames.slice(8)
        });
      }

      // Neutral fan base
      const neutralFanBase = { 
        team: 'neutral', 
        teamData: null,
        names: ['FootballFan', 'UEFALover', 'ChampionsFan', 'SoccerExpert'] 
      };

      // 50/50 split: even counter = team fans, odd counter = neutral
      const isTeamFan = messageCounter % 2 === 0;
      messageCounter++;

      let fanBase, username, message;

      if (isTeamFan && teamFanBases.length > 0) {
        // Team fan message
        fanBase = teamFanBases[Math.floor(Math.random() * teamFanBases.length)];
        username = `${fanBase.names[Math.floor(Math.random() * fanBase.names.length)]}${Math.floor(Math.random() * 999)}`;
        
        const td = fanBase.teamData;
        const messages = [
          `${td.chants[0]} We need a goal!`,
          `${td.name} should shoot more often`,
          `The ${td.name} attack is terrifying!`,
          `${td.name} fans would be electric right now`,
          `We're getting closer! ${td.chants[0]}`,
          `${td.name} please work your magic`,
          `${td.name} runs are dangerous`,
          `Come on ${td.name}! Score!`
        ];
        message = messages[Math.floor(Math.random() * messages.length)];
      } else {
        // Neutral fan message
        fanBase = neutralFanBase;
        username = `${fanBase.names[Math.floor(Math.random() * fanBase.names.length)]}${Math.floor(Math.random() * 999)}`;
        
        const messages = [
          `What a Champions League night!`,
          `Both teams going for it`,
          `This is football at the highest level`,
          `The technical quality is insane`,
          `What a match to watch`,
          `Two heavyweights going toe to toe`,
          `European football at its finest`,
          `This is why we love the game`
        ];
        message = messages[Math.floor(Math.random() * messages.length)];
      }

      const newSimulatedMessage = {
        id: `sim-${Date.now()}`,
        username,
        message,
        timestamp: Date.now(),
        isOwn: false,
        team: fanBase.team
      };

      setChatMessages(prev => [...prev, newSimulatedMessage]);
    }, 3000);
  };
  // Map chat message team to home/away colors
  const colorForTeam = (team) => {
    const t = String(team || '').toLowerCase();
    
    // Check if team matches home team
    if (currentTeams.home && t === currentTeams.home.slug) {
      return teamColors.left;
    }
    
    // Check if team matches away team
    if (currentTeams.away && t === currentTeams.away.slug) {
      return teamColors.right;
    }
    
    // Neutral or unknown team
    return 'rgba(255,255,255,0.08)';
  };

  const styleForMessage = (message) => {
    // Use team colors for all messages including user's own
    const c = colorForTeam(message.team);
    return { background: c, borderColor: 'rgba(255,255,255,0.12)' };
  };

  const styleForAvatar = (message) => {
    // Use team colors for all avatars including user's own
    const c = colorForTeam(message.team);
    return { background: c };
  };
  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;

    // If user hasn't selected a team preference yet, show selector
    if (userTeamPreference === null) {
      setShowTeamSelector(true);
      return;
    }

    const messageText = newMessage.trim();
    setIsSending(true);

    // Determine user's team based on preference
    let userTeam = 'neutral';
    if (userTeamPreference === 'home' && currentTeams.home) {
      userTeam = currentTeams.home.slug;
    } else if (userTeamPreference === 'away' && currentTeams.away) {
      userTeam = currentTeams.away.slug;
    }

    // Add user's message immediately
    const displayName = effectiveUser?.name || effectiveUser?.username || (effectiveUser?.email ? String(effectiveUser.email).split('@')[0] : null) || 'You';
    const userMessage = {
      id: `user-${Date.now()}`,
      username: displayName,
      message: messageText,
      timestamp: Date.now(),
      isOwn: true,
      team: userTeam
    };

    setChatMessages(prev => [...prev, userMessage]);
    setNewMessage('');

    // Simulate sending delay
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsSending(false);

    // Simulate occasional replies from current match team fans
    if (Math.random() < 1) {
      setTimeout(() => {
        // Pick a random team from current match
        const teams = [];
        if (currentTeams.home) teams.push(currentTeams.home);
        if (currentTeams.away) teams.push(currentTeams.away);
        
        if (teams.length === 0) return; // No teams detected
        
        const selectedTeam = teams[Math.floor(Math.random() * teams.length)];
        const fanNames = selectedTeam.fanNames.slice(8); // Use second half for variety

        const replies = [
          `I agree! ${selectedTeam.chants[0]} ${selectedTeam.hashtags[0]}`,
          `Well said! ${selectedTeam.name} will win this!`,
          `Exactly! We need to believe!`,
          `True that! ${selectedTeam.name} will score!`,
          `${selectedTeam.chants[1]} I agree!`,
          `Well spoken! ${selectedTeam.name} quality will show!`,
          `Exactly! ${selectedTeam.fullName} will prevail!`,
          `True! ${selectedTeam.name} is the key!`
        ];

        const botMessage = {
          id: `bot-${Date.now()}`,
          username: fanNames[Math.floor(Math.random() * fanNames.length)] + Math.floor(Math.random() * 99),
          message: replies[Math.floor(Math.random() * replies.length)],
          timestamp: Date.now(),
          isOwn: false,
          team: selectedTeam.slug
        };

        setChatMessages(prev => [...prev, botMessage]);
      }, 1000 + Math.random() * 2000);
    }
  };


  const dummy = useMemo(() => ({
    stats: [
      { k: 'Possession', home: '54%', away: '46%' },
      { k: 'Shots (On target)', home: '12 (5)', away: '9 (3)' },
      { k: 'Corners', home: '6', away: '4' },
      { k: 'Fouls', home: '8', away: '10' },
      { k: 'Yellow cards', home: '1', away: '2' },
    ],
    events: [
      { min: 12, type: 'Goal', team: 'Home', player: 'A. Forward' },
      { min: 28, type: 'Yellow', team: 'Away', player: 'M. Midfielder' },
      { min: 51, type: 'Sub', team: 'Home', player: 'D. Striker → J. Rookie' },
      { min: 73, type: 'Goal', team: 'Away', player: 'S. Winger' },
      { min: 85, type: 'VAR', team: 'Home', player: '—' },
    ],
    betting: [
      { market: 'Match Winner', home: '1.95', draw: '3.25', away: '3.90' },
      { market: 'Over/Under 2.5', over: '2.05', under: '1.75' },
      { market: 'Both Teams to Score', yes: '1.85', no: '1.95' },
    ],
    lineup: {
      home: {
        team: 'Home', formation: '4-3-3',
        starters: [
          { no: 1, name: 'A. Keeper', pos: 'GK' },
          { no: 2, name: 'B. Rightback', pos: 'RB' },
          { no: 4, name: 'C. Centerback', pos: 'CB' },
          { no: 5, name: 'D. Centerback', pos: 'CB' },
          { no: 3, name: 'E. Leftback', pos: 'LB' },
          { no: 6, name: 'F. Midfield', pos: 'CM' },
          { no: 8, name: 'G. Midfield', pos: 'CM' },
          { no: 10, name: 'H. Playmaker', pos: 'AM' },
          { no: 7, name: 'I. Winger', pos: 'RW' },
          { no: 9, name: 'J. Striker', pos: 'ST' },
          { no: 11, name: 'K. Winger', pos: 'LW' },
        ],
        subs: [
          { no: 12, name: 'L. Sub GK', pos: 'GK' },
          { no: 13, name: 'M. Defender', pos: 'DF' },
          { no: 14, name: 'N. Midfield', pos: 'MF' },
          { no: 15, name: 'O. Forward', pos: 'FW' },
        ]
      },
      away: {
        team: 'Away', formation: '4-2-3-1',
        starters: [
          { no: 1, name: 'P. Keeper', pos: 'GK' },
          { no: 22, name: 'Q. RB', pos: 'RB' },
          { no: 4, name: 'R. CB', pos: 'CB' },
          { no: 15, name: 'S. CB', pos: 'CB' },
          { no: 27, name: 'T. LB', pos: 'LB' },
          { no: 6, name: 'U. DM', pos: 'DM' },
          { no: 18, name: 'V. DM', pos: 'DM' },
          { no: 19, name: 'W. RW', pos: 'RW' },
          { no: 10, name: 'X. AM', pos: 'AM' },
          { no: 21, name: 'Y. LW', pos: 'LW' },
          { no: 9, name: 'Z. ST', pos: 'ST' },
        ],
        subs: [
          { no: 12, name: 'AA. GK', pos: 'GK' },
          { no: 13, name: 'BB. DF', pos: 'DF' },
          { no: 17, name: 'CC. MF', pos: 'MF' },
          { no: 23, name: 'DD. FW', pos: 'FW' },
        ]
      }
    }
  }), []);

  const baseWidth = isFullscreen ? 360 : 320;
  const eventsWidth = isFullscreen ? 520 : 440;
  const lineupWidth = isFullscreen ? 560 : 480;
  const panelWidth = tab === 'lineup' ? lineupWidth : (tab === 'events' ? eventsWidth : baseWidth);
  // Report width once; let the parent container's CSS transition handle the smoothness.
  React.useEffect(() => {
    try { onWidthChange && onWidthChange(open ? panelWidth : 0); } catch { }
  }, [open, panelWidth, onWidthChange]);
  // Leave space above the bottom overlay (ticker) with a small visual gap.
  const bottomOffset = isFullscreen ? 102 : 88; // px

  React.useEffect(() => {
    let cancelled = false;
    const base = (process.env.REACT_APP_API_BASE || '/dev');
    if (!(open && effectiveMatchId && tab === 'stats')) return;
    setStatsLoading(true); setStatsError('');
    fetch(`${base}/matches/stats/team?match_id=${encodeURIComponent(effectiveMatchId)}`, { headers: { accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        if (items.length >= 2) setTeamStats({ a: items[0], b: items[1] }); else setTeamStats(null);
      })
      .catch(() => { if (!cancelled) setStatsError('Failed to load stats'); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [open, effectiveMatchId, tab]);

  // Fetch lineups (both teams) and enrich with player images (from events) when lineup tab is open
  React.useEffect(() => {
    let cancelled = false;
    const base = (process.env.REACT_APP_API_BASE || '/dev');
    if (!(open && effectiveMatchId && tab === 'lineup')) return;
    setLineupsLoading(true); setLineupsError('');
    (async () => {
      try {
        // Fetch lineups
        const res = await fetch(`${base}/matches/stats/lineups?match_id=${encodeURIComponent(effectiveMatchId)}`, { headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        // Build a player -> image map using events (fetch if not loaded)
        let imageMap = new Map();
        // Build teamId -> logo map
        const logoByTeamId = new Map();
        try {
          if (!events || events.length === 0) {
            const evr = await fetch(`${base}/matches/stats/events?match_id=${encodeURIComponent(effectiveMatchId)}`, { headers: { accept: 'application/json' } });
            if (evr.ok) {
              const evd = await evr.json();
              const evItems = Array.isArray(evd?.items) ? evd.items : [];
              evItems.forEach(e => {
                if (e.player_id && e.player_image_url) imageMap.set(Number(e.player_id), e.player_image_url);
                if (e.related_player_id && e.related_player_image_url) imageMap.set(Number(e.related_player_id), e.related_player_image_url);
                if (e.team_id && e.team_logo_url && !logoByTeamId.has(Number(e.team_id))) logoByTeamId.set(Number(e.team_id), e.team_logo_url);
              });
            }
          } else {
            events.forEach(e => {
              if (e.player_id && e.player_image_url) imageMap.set(Number(e.player_id), e.player_image_url);
              if (e.related_player_id && e.related_player_image_url) imageMap.set(Number(e.related_player_id), e.related_player_image_url);
              if (e.team_id && e.team_logo_url && !logoByTeamId.has(Number(e.team_id))) logoByTeamId.set(Number(e.team_id), e.team_logo_url);
            });
          }
        } catch { }
        try {
          // Also consider teamStats (if loaded) for logos
          if (teamStats) {
            if (teamStats.a?.team_id && teamStats.a?.team_logo_url) logoByTeamId.set(Number(teamStats.a.team_id), teamStats.a.team_logo_url);
            if (teamStats.b?.team_id && teamStats.b?.team_logo_url) logoByTeamId.set(Number(teamStats.b.team_id), teamStats.b.team_logo_url);
          }
        } catch { }
        // Map to our lineup model
        const sides = items.map(teamLineup => {
          const apps = Array.isArray(teamLineup?.appearances) ? teamLineup.appearances : [];
          const starters = apps.filter(a => a.is_starting);
          const bench = apps.filter(a => !a.is_starting);
          const mapped = starters.map(a => ({
            no: a.shirt_number,
            pos: a.position,
            name: a.player_name || '',
            img: a.player_image_url || imageMap.get(Number(a.player_id)) || null,
            out: a.minute_out != null,
          }));
          const mappedSubs = bench.map(a => ({
            no: a.shirt_number,
            pos: a.position,
            name: a.player_name || '',
            img: a.player_image_url || imageMap.get(Number(a.player_id)) || null,
            in: a.minute_in != null && a.minute_in > 0,
            out: a.minute_out != null,
            minute_in: a.minute_in,
            minute_out: a.minute_out,
          }));
          return {
            team_id: teamLineup.team_id,
            formation: teamLineup.formation || '',
            coach_name: teamLineup.coach_name || '',
            team_logo_url: logoByTeamId.get(Number(teamLineup.team_id)) || null,
            starters: mapped,
            subs: mappedSubs,
          };
        });
        // Prefer consistent order: lower team_id first
        sides.sort((a, b) => (a.team_id || 0) - (b.team_id || 0));
        const home = sides[0] || { formation: '', starters: [] };
        const away = sides[1] || { formation: '', starters: [] };
        if (!cancelled) setLineups({ home, away });
      } catch (e) {
        if (!cancelled) setLineupsError('Failed to load lineups');
      } finally {
        if (!cancelled) setLineupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, effectiveMatchId, tab, events, teamStats]);

  // Fetch match meta to derive team home colors for stats
  React.useEffect(() => {
    let cancelled = false;
    const base = (process.env.REACT_APP_API_BASE || '/dev');
    if (!(open && effectiveMatchId && tab === 'stats')) return;
    (async () => {
      try {
        const res = await fetch(`${base}/matches?limit=50`, { headers: { accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        const m = items.find(x => Number(x?.id) === Number(effectiveMatchId));
        if (cancelled) return;
        if (m) setMatchMeta(m);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, effectiveMatchId, tab]);

  // Derive team home colors from match meta and stats teams
  React.useEffect(() => {
    const toHomeColor = (meta, teamSlug) => {
      try {
        if (!meta || !teamSlug) return null;
        const bucket = meta[teamSlug];
        if (!Array.isArray(bucket)) return null;
        // pick label including 'home_kit' and not goalkeeper
        const isHome = (lbl) => /home[_-]?kit/i.test(String(lbl)) && !/goalkeeper/i.test(String(lbl));
        const entry = bucket.find(e => isHome(e?.label)) || bucket.find(e => /home/i.test(String(e?.label)) && !/goalkeeper/i.test(String(e?.label))) || bucket[0];
        const color = entry?.color || null;
        return color;
      } catch { return null; }
    };
    if (matchMeta && teamStats) {
      const leftSlug = teamStats.a?.team || '';
      const rightSlug = teamStats.b?.team || '';
      const left = toHomeColor(matchMeta, leftSlug) || '#22d3ee';
      const right = toHomeColor(matchMeta, rightSlug) || '#818cf8';
      setTeamColors({ left, right });
    }
  }, [matchMeta, teamStats]);

  // Fetch match events when Events tab is open
  React.useEffect(() => {
    let cancelled = false;
    const base = (process.env.REACT_APP_API_BASE || '/dev');
    if (!(open && effectiveMatchId && tab === 'events')) return;
    setEventsLoading(true); setEventsError('');
    fetch(`${base}/matches/stats/events?match_id=${encodeURIComponent(effectiveMatchId)}`, { headers: { accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        // sort by period then minute
        const periodOrder = { '1H': 1, '2H': 2, 'ET': 3 };
        items.sort((a, b) => (periodOrder[a.period] || 0) - (periodOrder[b.period] || 0) || (a.minute || 0) - (b.minute || 0));
        setEvents(items);
      })
      .catch(() => { if (!cancelled) setEventsError('Failed to load events'); })
      .finally(() => { if (!cancelled) setEventsLoading(false); });
    return () => { cancelled = true; };
  }, [open, effectiveMatchId, tab]);

  // Helpers for lineup pitch rendering (shortName removed as unused)

  const PitchLineup = ({ side, theme = 'home', height = 360 }) => {
    const formation = String(side?.formation || '').trim();
    const tokens = formation.split('-').map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n) && n > 0);
    const starters = Array.isArray(side?.starters) ? side.starters.slice() : [];
    // Extract GK
    let gkIdx = starters.findIndex((p) => String(p.pos || '').toUpperCase().includes('GK'));
    if (gkIdx < 0) gkIdx = 0;
    const gk = starters[gkIdx];
    const others = starters.filter((_, i) => i !== gkIdx);
    // Group others roughly by line: DEF / MID / ATT
    const norm = (s) => String(s || '').toUpperCase();
    const groupOf = (pos) => {
      const p = norm(pos);
      if (p.includes('WB') || p.includes('RB') || p.includes('LB') || p.includes('CB') || p.includes('DF')) return 'DEF';
      if (p.includes('DM') || p.includes('CM') || p.includes('AM') || p.includes('MF') || p.includes('MID')) return 'MID';
      if (p.includes('LW') || p.includes('RW') || p.includes('WF') || p.includes('ST') || p.includes('FW') || p.includes('ATT') || p.includes('F')) return 'ATT';
      return 'MID';
    };
    const defArr = others.filter(p => groupOf(p.pos) === 'DEF');
    const midArr = others.filter(p => groupOf(p.pos) === 'MID');
    const attArr = others.filter(p => groupOf(p.pos) === 'ATT');
    // Build rows per formation: first row => DEF, last row => ATT, middle => MID (fallback from any group if needed)
    const take = (arr, n) => { const out = []; for (let i = 0; i < n && arr.length; i++) out.push(arr.shift()); return out; };
    const rows = tokens.map((cnt, i) => {
      let row = [];
      if (i === 0) row = take(defArr, cnt);
      else if (i === tokens.length - 1) row = take(attArr, cnt);
      else row = take(midArr, cnt);
      // If not enough, pull from other pools
      const pools = [midArr, attArr, defArr];
      for (const pool of pools) { while (row.length < cnt && pool.length) row.push(pool.shift()); }
      return row;
    });
    // Horizontal ordering within a row (LB ... RB, LW...RW)
    const hOrder = (p) => {
      const pos = norm(p?.pos);
      if (pos.includes('LB') || pos.includes('LWB')) return -3;
      if (pos.includes('LCB')) return -2;
      if (pos.includes('CB')) return 0;
      if (pos.includes('RCB')) return 2;
      if (pos.includes('RB') || pos.includes('RWB')) return 3;
      if (pos.includes('LW')) return -3;
      if (pos.includes('LM')) return -2;
      if (pos.includes('AM') || pos.includes('CAM')) return 0;
      if (pos.includes('RM')) return 2;
      if (pos.includes('RW')) return 3;
      if (pos.includes('ST') || pos.includes('CF')) return 0;
      return 0;
    };
    // Compute Y positions (%). Home GK near bottom; Away GK near top.
    // Expand usable vertical space to place GK at the very start of the pitch for away
    const topY = 4, bottomY = 88;
    const yForRow = (i) => {
      const rowsCount = Math.max(1, tokens.length);
      // Use more of the pitch height so rows are well spaced and "fit" the field
      const rowsTop = 20;   // percent from top
      const rowsBottom = 80; // percent from top
      if (theme === 'home') {
        if (rowsCount === 1) return (rowsTop + rowsBottom) / 2;
        // i=0 (DEF) near bottom, last row (ATT) near top
        let y = rowsBottom - (i * (rowsBottom - rowsTop) / (rowsCount - 1));
        // Ensure DEF line isn't too close to the GK
        if (i === 0) {
          const minGap = 12; // percent points gap from GK
          const targetMax = (gkY - minGap);
          y = Math.min(y, targetMax);
          y = Math.max(y, rowsTop + 4); // keep within pitch
        }
        return y;
      }
      // Away from top towards center, keep DEF well separated from GK
      if (rowsCount === 1) return (rowsTop + rowsBottom) / 2;
      const minGap = 18; // percent points gap from GK to DEF for away
      const defY = Math.min(Math.max(gkY + minGap, rowsTop + 4), rowsBottom - 6);
      // Special spacing for common 4-2-3-1 (rowsCount=4): DEF, MID1, MID2, ATT
      if (rowsCount === 4) {
        if (i === 0) return defY;
        if (i === 1) {
          // Bring MID1 closer to DEF, but still balanced
          const y1 = defY + (rowsBottom - defY) * 0.26;
          return Math.min(Math.max(y1, defY + 8), rowsBottom - 8);
        }
        if (i === 2) {
          const y2 = defY + (rowsBottom - defY) * 0.55;
          return Math.min(y2, rowsBottom - 6);
        }
        // i === 3 -> ATT
        return rowsBottom - 4;
      }
      // Default spacing for other formations with gentle ease-out
      const t = i / (rowsCount - 1);
      const eased = Math.sqrt(Math.max(0, Math.min(1, t)));
      let y = rowsTop + eased * (rowsBottom - rowsTop);
      if (i === 0) {
        const targetMin = (gkY + minGap);
        y = Math.max(y, targetMin);
        y = Math.min(y, rowsBottom - 6);
      }
      return y;
    };
    // Keep GK a bit inside the pitch so the name doesn't get clipped
    const gkOffsetHome = 6; // percent
    const gkOffsetAway = 0.5; // percent (very close to top for away as requested)
    const gkY = theme === 'home' ? (bottomY - gkOffsetHome) : (topY + gkOffsetAway);
    const kitGrad = theme === 'home' ? 'from-cyan-400 to-indigo-400' : 'from-indigo-400 to-fuchsia-400';

    return (
      <div className="relative w-full rounded-xl overflow-hidden border border-white/15 shadow-[0_8px_24px_rgba(0,0,0,0.25)]" style={{ height }}>
        {/* Pitch background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.12),rgba(13,148,136,0.08))]" />
        <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(255,255,255,0.05)_8%,transparent_8%,transparent_16%,rgba(255,255,255,0.05)_16%)] bg-[length:100%_24px] opacity-30" />
        {/* Pitch lines */}
        <div className="absolute inset-2 rounded-md border border-white/20" />
        <div className="absolute inset-y-2 left-1/2 -translate-x-1/2 w-px bg-white/20" />
        {/* Center circle */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-white/20" />
        {/* Penalty boxes */}
        {/* Top/bottom boxes depending on orientation */}
        <div className="absolute left-[18%] right-[18%] top-2 h-12 border border-white/20 rounded-sm" />
        <div className="absolute left-[18%] right-[18%] bottom-2 h-12 border border-white/20 rounded-sm" />

        {/* GK */}
        {gk && (
          <div className="absolute" style={{ left: '50%', transform: 'translateX(-50%)', top: `${gkY}%` }}>
            <div className="relative mx-auto w-11 h-11">
              <div className={`w-full h-full rounded-full ring-1 ring-white/40 bg-gradient-to-br ${kitGrad} flex items-center justify-center text-black font-bold text-xs overflow-hidden`}>
                {gk.img ? <img src={gk.img} alt="GK" className="w-full h-full object-cover" /> : <span>{gk.no}</span>}
              </div>
              {gk.out && (
                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white/90 flex items-center justify-center shadow">
                  <FontAwesomeIcon icon={faArrowDown} className="text-red-500 text-[10px]" />
                </div>
              )}
            </div>
            <div className="mt-1 text-[10px] text-white/80 text-center whitespace-nowrap">{gk.no}</div>
          </div>
        )}
        {/* Rows */}
        {rows.map((row, i) => {
          const y = yForRow(i);
          const n = Math.max(1, row.length);
          // Sort row across width by horizontal order
          const sorted = row.slice().sort((a, b) => hOrder(a) - hOrder(b));
          return (
            <div key={`row-${i}`} className="absolute w-full" style={{ top: `${y}%` }}>
              <div className="relative w-full">
                {sorted.map((p, j) => {
                  const margin = 8; // keep players away from touchline but allow wider spread
                  const x = margin + ((j + 1) / (n + 1)) * (100 - 2 * margin); // percent
                  return (
                    <div key={`${p.no}-${p.pos}-${j}`} className="absolute" style={{ left: `${x}%`, transform: 'translateX(-50%)' }}>
                      <div className="relative mx-auto w-10 h-10">
                        <div className={`w-full h-full rounded-full ring-1 ring-white/40 bg-gradient-to-br ${kitGrad} flex items-center justify-center text-black font-bold text-[11px] overflow-hidden`}>
                          {p.img ? <img src={p.img} alt="player" className="w-full h-full object-cover" /> : <span>{p.no}</span>}
                        </div>
                        {p.out && (
                          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white/90 flex items-center justify-center shadow">
                            <FontAwesomeIcon icon={faArrowDown} className="text-red-500 text-[9px]" />
                          </div>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] text-white/80 text-center whitespace-nowrap">{p.no}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="right-menu"
          initial={{ x: panelWidth, opacity: 0, width: panelWidth }}
          animate={{ x: 0, opacity: 1, width: panelWidth }}
          exit={{ x: panelWidth, opacity: 0, width: panelWidth }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="absolute top-0 right-0 z-[2147483646]"
          style={{ width: panelWidth, bottom: bottomOffset }}
        >
          <div className="h-full w-full relative bg-gradient-to-b from-[rgba(26,35,50,0.9)] to-[rgba(17,24,39,0.9)] text-white border-l border-white/10 supports-[backdrop-filter]:backdrop-blur backdrop-blur-md shadow-2xl rounded-tl-xl rounded-bl-xl overflow-hidden">
            {/* Floating header */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between gap-2 px-3 py-2 h-12 border-b border-white/10 bg-[rgba(10,15,25,0.95)] backdrop-blur-md shadow">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-white/10 flex-shrink-0">
                  {effectiveUser?.avatar_url ? <img src={effectiveUser.avatar_url} alt="me" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="text-sm font-semibold tracking-wide truncate">
                  {tab === 'stats' ? 'Match Stats' :
                    tab === 'events' ? 'Match Events' :
                      tab === 'betting' ? 'Betting' :
                        tab === 'lineup' ? 'Team Lineup' :
                          'Live Chat'}
                </div>
              </div>
              <button type="button" onClick={onClose} className="px-2 py-1 text-xs rounded border border-white/20 bg-white/10 hover:bg-white/20">
                Close
              </button>
            </div>
            {/* Body with right-side vertical toolbar */}
            <div className="h-full w-full flex">
              <div className="flex-1 overflow-y-auto p-3 pt-12 space-y-4 pr-14 scrollbar-thin">
                {tab === 'stats' && (
                  <div className="space-y-3">
                    {(() => {
                      if (!effectiveMatchId) return (<div className="text-sm text-white/70">No match id.</div>);
                      if (statsLoading) return (<div className="text-sm text-white/60">Loading stats…</div>);
                      if (statsError) return (<div className="text-sm text-red-300">{statsError}</div>);
                      if (!teamStats) return (<div className="text-sm text-white/70">No stats available.</div>);
                      const A = teamStats.a; const B = teamStats.b;
                      const slugA = (A.team || 'team_a');
                      const slugB = (B.team || 'team_b');
                      const asPct = (n) => (n != null ? `${n}%` : '0%');
                      const rows = [
                        { k: 'Possession', home: asPct(A.possession_pct), away: asPct(B.possession_pct) },
                        { k: 'Shots (On target)', home: `${A.shots} (${A.shots_on_target})`, away: `${B.shots} (${B.shots_on_target})` },
                        { k: 'Passes', home: `${A.passes}`, away: `${B.passes}` },
                        { k: 'Pass accuracy', home: asPct(A.pass_accuracy_pct), away: asPct(B.pass_accuracy_pct) },
                        { k: 'Fouls', home: `${A.fouls}`, away: `${B.fouls}` },
                        { k: 'Yellow cards', home: `${A.yellow_cards}`, away: `${B.yellow_cards}` },
                        { k: 'Red cards', home: `${A.red_cards}`, away: `${B.red_cards}` },
                        { k: 'Offsides', home: `${A.offsides}`, away: `${B.offsides}` },
                        { k: 'Corners', home: `${A.corners}`, away: `${B.corners}` },
                      ];
                      // Top title and logos (inside content area)
                      const TopTitle = () => (
                        <div className="mb-3">
                          <div className="text-base font-semibold text-white">Match Stats</div>
                          <div className="mt-2 flex items-center justify-between">
                            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                              {A.team_logo_url ? <img src={A.team_logo_url} alt={slugA} className="w-7 h-7 object-contain" /> : <span className="text-xs text-white/70">A</span>}
                            </div>
                            <div className="text-white/60 text-xs">vs</div>
                            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                              {B.team_logo_url ? <img src={B.team_logo_url} alt={slugB} className="w-7 h-7 object-contain" /> : <span className="text-xs text-white/70">B</span>}
                            </div>
                          </div>
                        </div>
                      );
                      const SectionHeader = ({ title }) => (
                        <div className="mb-2">
                          <div className="text-[11px] uppercase tracking-wide text-white/80">{title}</div>
                        </div>
                      );
                      // render top title once above the first row
                      const content = [];
                      content.push(<TopTitle key="__title" />);
                      rows.forEach((row, idx) => {
                        const parsePrimaryNumber = (v) => {
                          const s = String(v || '').trim();
                          // Prefer percentage number if present
                          const pm = s.match(/([\d.]+)\s*%/);
                          if (pm) return parseFloat(pm[1]);
                          // Otherwise first integer sequence in the string
                          const nm = s.match(/(\d+)/);
                          if (nm) return parseInt(nm[1], 10);
                          return null;
                        };
                        const hVal = parsePrimaryNumber(row.home);
                        const aVal = parsePrimaryNumber(row.away);
                        let hp2 = 50;
                        let ap2 = 50;
                        if (hVal != null && aVal != null) {
                          const total = hVal + aVal;
                          if (total > 0) {
                            hp2 = Math.max(0, Math.min(100, (hVal / total) * 100));
                            ap2 = 100 - hp2;
                          }
                        }
                        content.push(
                          <div key={`row-${idx}`} className="rounded-xl border border-white/10 bg-white/5 p-3 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
                            <SectionHeader title={row.k} />
                            <>
                              <div className="w-full h-4 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/10">
                                <div className="h-full" style={{ width: `${hp2}%`, background: teamColors.left }} />
                                <div className="-mt-4 h-4 float-right" style={{ width: `${ap2}%`, background: teamColors.right }} />
                              </div>
                              <div className="mt-1 flex items-center justify-between text-[11px]">
                                <span className="text-white">{row.home}</span>
                                <span className="text-white">{row.away}</span>
                              </div>
                            </>
                          </div>
                        );
                      });
                      return content;
                    })()}
                  </div>
                )}
                {tab === 'events' && (
                  <div className="space-y-3">
                    {(() => {
                      if (!effectiveMatchId) return (<div className="text-sm text-white/70">No match id.</div>);
                      if (eventsLoading) return (<div className="text-sm text-white/60">Loading events…</div>);
                      if (eventsError) return (<div className="text-sm text-red-300">{eventsError}</div>);
                      if (!events || events.length === 0) return (<div className="text-sm text-white/70">No events.</div>);

                      // Build filter options
                      const uniqueTypes = Array.from(new Set(events.map(e => String(e.event_type || '').toUpperCase())));
                      const uniqueTeams = Array.from(new Map(events.map(e => [e.team, e.team_logo_url])).entries()) // [slug, logo]
                        .map(([team, logo]) => ({ team, logo }));

                      const filtered = events.filter(e => {
                        const tOk = (eventTypeFilter === 'all') || (String(e.event_type || '').toUpperCase() === eventTypeFilter);
                        const tmOk = (eventTeamFilter === 'all') || (String(e.team || '') === eventTeamFilter);
                        return tOk && tmOk;
                      });

                      const FilterBar = () => (
                        <div className="mb-2 space-y-2">
                          {/* Event type icons */}
                          <div className="flex items-center gap-2 overflow-x-auto pr-1">
                            <button
                              type="button"
                              onClick={() => setEventTypeFilter('all')}
                              className={`w-8 h-8 rounded-full border flex items-center justify-center ${eventTypeFilter === 'all' ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                              title="All"
                            >
                              <FontAwesomeIcon icon={faFlag} className="text-white/90" />
                            </button>
                            {uniqueTypes.map((T) => {
                              const t = String(T || '').toUpperCase();
                              const low = t.toLowerCase();
                              const isGoal = low.includes('goal');
                              const isPenalty = low.includes('penalty');
                              const isYellow = low.includes('yellow');
                              const isRed = low.includes('red');
                              const isSub = low.includes('sub');
                              const isVar = low.includes('var');
                              const icon = isGoal ? faFutbol : (isYellow || isRed) ? faSquare : isSub ? faRightLeft : isVar ? faVideo : faFlag;
                              const colorClass = isPenalty ? 'text-amber-300' : (isGoal ? 'text-emerald-300' : isYellow ? 'text-yellow-400' : isRed ? 'text-red-400' : isSub ? 'text-cyan-300' : 'text-white/90');
                              const active = (eventTypeFilter === t);
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setEventTypeFilter(t)}
                                  className={`w-8 h-8 rounded-full border flex items-center justify-center ${active ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                                  title={t.replace(/_/g, ' ')}
                                >
                                  {isPenalty ? <PenaltyIcon className={colorClass} /> : <FontAwesomeIcon icon={icon} className={colorClass} />}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 overflow-x-auto pr-1">
                            <button
                              type="button"
                              onClick={() => setEventTeamFilter('all')}
                              className={`px-2 py-1 rounded-full text-[11px] border ${eventTeamFilter === 'all' ? 'bg-white/20 border-white/40 text-white' : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'}`}
                            >
                              All teams
                            </button>
                            {uniqueTeams.map(({ team, logo }) => (
                              <button
                                key={team || 'team'}
                                type="button"
                                onClick={() => setEventTeamFilter(team)}
                                className={`w-8 h-8 rounded-full border ${eventTeamFilter === team ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'} flex items-center justify-center overflow-hidden`}
                                title={team}
                              >
                                {logo ? (<img src={logo} alt={team} className="w-7 h-7 object-contain" />) : <span className="text-[10px] text-white/70">{team?.slice(0, 2).toUpperCase()}</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      );

                      const list = filtered;

                      return [<FilterBar key="__filters" />].concat(list.map((ev, idx) => {
                        const t = String(ev.event_type || '').toLowerCase();
                        const isPenalty = t.includes('penalty');
                        const isGoal = t.includes('goal') && !isPenalty;
                        const isYellow = t.includes('yellow');
                        const isRed = t.includes('red');
                        const isSub = t.includes('sub');
                        const icon = isGoal ? faFutbol
                          : (isYellow || isRed) ? faSquare
                            : t.includes('sub') ? faRightLeft
                              : t.includes('var') ? faVideo
                                : faFlag;
                        const accent = isGoal
                          ? 'from-emerald-400 to-emerald-500'
                          : isPenalty
                            ? 'from-amber-400 to-yellow-400'
                            : isSub
                              ? 'from-sky-400 to-cyan-400'
                              : 'from-indigo-400 to-fuchsia-400';
                        // Style overrides for yellow card: make icon yellow and pill neutral for contrast
                        const pillBase = (isYellow || isRed || isPenalty) ? 'bg-white/10' : `bg-gradient-to-br ${accent}`;
                        const ringClass = isYellow ? 'ring-yellow-300/50' : isRed ? 'ring-red-300/50' : isPenalty ? 'ring-amber-300/50' : 'ring-white/30';
                        const iconColorClass = isYellow ? 'text-yellow-400' : isRed ? 'text-red-400' : isPenalty ? 'text-amber-300' : 'text-black/80';
                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, delay: idx * 0.03 }}
                            className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center gap-3 shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
                          >
                            <span className="text-[10px] px-2 py-1 rounded-full bg-white/10 border border-white/20 whitespace-nowrap">{ev.minute}'</span>
                            <div className={`w-8 h-8 rounded-full ${pillBase} flex items-center justify-center ring-1 ${ringClass}`}>
                              {isPenalty ? <PenaltyIcon className={`${iconColorClass}`} /> : <FontAwesomeIcon icon={icon} className={`${iconColorClass}`} />}
                            </div>
                            {/* Player avatar(s) */}
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                {ev.player_image_url ? (
                                  <img src={ev.player_image_url} alt={ev.player_name || 'player'} className="w-full h-full object-cover" />
                                ) : null}
                              </div>
                              {ev.related_player_image_url && (
                                <div className="w-7 h-7 -ml-4 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                  <img src={ev.related_player_image_url} alt={ev.related_player_name || 'related'} className="w-full h-full object-cover" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs text-white/80 uppercase tracking-wide">{String(ev.event_type || '').replace(/_/g, ' ')}</div>
                              <div className="text-sm font-semibold truncate flex items-center gap-2">
                                <span className="text-emerald-300">{isSub ? 'IN' : ''}</span>
                                <span className="text-white">{ev.player_name}</span>
                              </div>
                              {ev.related_player_name && (
                                <div className="text-[11px] truncate flex items-center gap-2">
                                  <span className="text-red-300">{isSub ? 'OUT' : ''}</span>
                                  <span className="text-white/80">{ev.related_player_name}</span>
                                </div>
                              )}
                            </div>
                            <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-white/10 flex-shrink-0">
                              {ev.team_logo_url ? <img src={ev.team_logo_url} alt={ev.team || ''} className="w-full h-full object-contain" /> : null}
                            </div>
                          </motion.div>
                        );
                      }));
                    })()}
                  </div>
                )}
                {tab === 'betting' && (
                  <div className="space-y-4">
                    {dummy.betting.map((mkt, idx) => (
                      <div key={idx} className="rounded-xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-3 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
                        <div className="text-xs text-white/70 mb-2">{mkt.market}</div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          {Object.entries(mkt).filter(([k]) => k !== 'market').map(([k, v]) => (
                            <button
                              key={k}
                              type="button"
                              onClick={() => { setBetPick({ market: mkt.market, selection: k, odds: v }); setShowBetProviders(true); }}
                              className="px-2 py-2 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 text-white/90 transition-colors"
                              title={`${mkt.market} - ${k}`}
                            >
                              <div className="text-[10px] uppercase text-white/60">{k}</div>
                              <div className="font-semibold text-white">{v}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {showBetProviders && (
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-white/80">Choose provider for: <span className="font-semibold text-white">{betPick?.market} - {betPick?.selection} ({betPick?.odds})</span></div>
                          <button type="button" className="text-[11px] px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20" onClick={() => setShowBetProviders(false)}>Close</button>
                        </div>
                        <div className="flex flex-col gap-2">
                          {bettingProviders.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                // Keep providers visible after click; user can close manually via the Close button
                                if (p.allowsIframe === true && onOpenWebview) {
                                  onOpenWebview(p.url, p);
                                } else {
                                  window.open(p.url, '_blank', 'noopener');
                                }
                              }}
                              className="w-full flex items-center justify-between px-2 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-7 h-7 rounded bg-white/10 border border-white/20 overflow-hidden flex items-center justify-center">
                                  {p.logo ? <img src={p.logo} alt={p.name} className="w-6 h-6 object-contain" /> : <span className="text-xs text-white/80">{p.name[0]}</span>}
                                </div>
                                <div className="text-sm text-white/90 truncate">{p.name}</div>
                              </div>
                              <div className="text-[10px] text-white/60 ml-3 shrink-0">Visit</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tab === 'lineup' && (
                  <div className="space-y-4">
                    {!lineups && lineupsLoading && <div className="text-sm text-white/60">Loading lineups…</div>}
                    {lineupsError && <div className="text-sm text-red-300">{lineupsError}</div>}
                    {lineups && (
                      <div className="grid grid-cols-1 gap-4">
                        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                {lineups.home.team_logo_url ? <img src={lineups.home.team_logo_url} alt="home" className="w-full h-full object-contain" /> : null}
                              </div>
                              <div className="text-sm font-semibold text-white/90">Home</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20">{lineups.home.formation || '—'}</div>
                            </div>
                          </div>
                          <PitchLineup side={lineups.home} theme="home" height={isFullscreen ? 460 : 380} />
                          {lineups.home.coach_name && (
                            <div className="mt-2 text-[11px] text-white/70">Coach: <span className="text-white/90">{lineups.home.coach_name}</span></div>
                          )}
                          {Array.isArray(lineups.home.subs) && lineups.home.subs.length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] uppercase tracking-wide text-white/60 mb-1">Bench</div>
                              <div className="flex flex-wrap gap-2">
                                {lineups.home.subs.map((s, i) => (
                                  <div key={`home-sub-${i}`} className="flex items-center gap-2 px-2 py-1 rounded-lg bg-white/5 border border-white/10">
                                    <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                      {s.img ? <img src={s.img} alt={s.name || 'sub'} className="w-full h-full object-cover" /> : null}
                                    </div>
                                    <div className="text-[11px] text-white/90 min-w-0 truncate">{s.name || `#${s.no}`}</div>
                                    <div className="flex items-center gap-1 ml-1">
                                      {s.in && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><FontAwesomeIcon icon={faArrowUp} />{s.minute_in}'</span>
                                      )}
                                      {s.out && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-red-300"><FontAwesomeIcon icon={faArrowDown} />{s.minute_out}'</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                {lineups.away.team_logo_url ? <img src={lineups.away.team_logo_url} alt="away" className="w-full h-full object-contain" /> : null}
                              </div>
                              <div className="text-sm font-semibold text-white/90">Away</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20">{lineups.away.formation || '—'}</div>
                            </div>
                          </div>
                          <PitchLineup side={lineups.away} theme="away" height={isFullscreen ? 460 : 380} />
                          {lineups.away.coach_name && (
                            <div className="mt-2 text-[11px] text-white/70">Coach: <span className="text-white/90">{lineups.away.coach_name}</span></div>
                          )}
                          {Array.isArray(lineups.away.subs) && lineups.away.subs.length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] uppercase tracking-wide text-white/60 mb-1">Bench</div>
                              <div className="flex flex-wrap gap-2">
                                {lineups.away.subs.map((s, i) => (
                                  <div key={`away-sub-${i}`} className="flex items-center gap-2 px-2 py-1 rounded-lg bg-white/5 border border-white/10">
                                    <div className="w-6 h-6 rounded-full overflow-hidden border border-white/20 bg-white/10">
                                      {s.img ? <img src={s.img} alt={s.name || 'sub'} className="w-full h-full object-cover" /> : null}
                                    </div>
                                    <div className="text-[11px] text-white/90 min-w-0 truncate">{s.name || `#${s.no}`}</div>
                                    <div className="flex items-center gap-1 ml-1">
                                      {s.in && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><FontAwesomeIcon icon={faArrowUp} />{s.minute_in}'</span>
                                      )}
                                      {s.out && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-red-300"><FontAwesomeIcon icon={faArrowDown} />{s.minute_out}'</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tab === 'chat' && (
                  <div className="h-full flex flex-col">
                    {/* Chat Messages */}
                    <div
                      ref={chatContainerRef}
                      className="flex-1 overflow-y-auto space-y-2 mb-3 pr-2 scrollbar-thin"
                    >
                      <AnimatePresence initial={false}>
                        {chatMessages.map((msg, index) => (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className={"p-2 rounded-lg border ml-0 mr-0"}
                            style={styleForMessage(msg)}
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs" style={styleForAvatar(msg)}>
                                {msg.username.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-white/80 font-semibold truncate max-w-[50%]">{msg.username}</span>
                                  <span className="text-[10px] text-white/60">
                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <div className="text-sm text-white/90 break-words">
                                  {msg.message}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>

                    {/* User Team Preference Indicator */}
                    {userTeamPreference && (
                      <div className="mb-2 px-3 py-2 rounded-lg border flex items-center justify-between" style={{
                        background: userTeamPreference === 'home' ? `${teamColors.left}20` :
                                   userTeamPreference === 'away' ? `${teamColors.right}20` :
                                   'rgba(255,255,255,0.05)',
                        borderColor: userTeamPreference === 'home' ? `${teamColors.left}40` :
                                    userTeamPreference === 'away' ? `${teamColors.right}40` :
                                    'rgba(255,255,255,0.1)'
                      }}>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden" style={{
                            background: userTeamPreference === 'home' ? teamColors.left :
                                       userTeamPreference === 'away' ? teamColors.right :
                                       'rgba(255,255,255,0.1)'
                          }}>
                            {userTeamPreference === 'home' && teamStats?.a?.team_logo_url ? (
                              <img 
                                src={teamStats.a.team_logo_url} 
                                alt={currentTeams.home?.name}
                                className="w-5 h-5 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : userTeamPreference === 'away' && teamStats?.b?.team_logo_url ? (
                              <img 
                                src={teamStats.b.team_logo_url} 
                                alt={currentTeams.away?.name}
                                className="w-5 h-5 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="text-xs font-bold">
                                {userTeamPreference === 'home' && currentTeams.home ? currentTeams.home.name.charAt(0) :
                                 userTeamPreference === 'away' && currentTeams.away ? currentTeams.away.name.charAt(0) :
                                 '⚽'}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-white/70">
                            Chatting as: <span className="text-white font-semibold">
                              {userTeamPreference === 'home' && currentTeams.home ? `${currentTeams.home.name} Fan` :
                               userTeamPreference === 'away' && currentTeams.away ? `${currentTeams.away.name} Fan` :
                               'Neutral Fan'}
                            </span>
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUserTeamPreference(null);
                            setShowTeamSelector(true);
                          }}
                          className="text-xs hover:text-white/80 transition-colors"
                          style={{
                            color: userTeamPreference === 'home' ? teamColors.left :
                                  userTeamPreference === 'away' ? teamColors.right :
                                  'rgba(34,211,238,1)'
                          }}
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={userTeamPreference ? "Type your message..." : "Choose your team first..."}
                        className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm placeholder-white/50 focus:outline-none focus:border-cyan-400/50"
                        disabled={isSending}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || isSending}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:from-cyan-600 hover:to-blue-600 transition-all"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </button>
                    </form>

                    {/* Team Selector Modal */}
                    {showTeamSelector && (
                      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
                        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 max-w-sm w-full mx-4 border border-white/10">
                          <h3 className="text-xl font-bold text-white mb-2 text-center">Choose Your Side</h3>
                          <p className="text-sm text-white/70 mb-6 text-center">
                            Select which team you support or stay neutral
                          </p>
                          
                          <div className="space-y-3">
                            {/* Home Team Button */}
                            {currentTeams.home && (
                              <button
                                onClick={() => {
                                  setUserTeamPreference('home');
                                  setShowTeamSelector(false);
                                  // Trigger message send after selection
                                  setTimeout(() => {
                                    const form = document.querySelector('form');
                                    if (form) form.requestSubmit();
                                  }, 100);
                                }}
                                style={{
                                  background: `linear-gradient(to right, ${teamColors.left}40, ${teamColors.left}20)`,
                                  borderColor: `${teamColors.left}80`
                                }}
                                className="w-full p-4 rounded-lg border-2 hover:brightness-110 transition-all group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden group-hover:scale-110 transition-transform">
                                    {teamStats?.a?.team_logo_url ? (
                                      <img 
                                        src={teamStats.a.team_logo_url} 
                                        alt={currentTeams.home.name}
                                        className="w-10 h-10 object-contain"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center text-lg font-bold ${teamStats?.a?.team_logo_url ? 'hidden' : 'flex'}`}>
                                      {currentTeams.home.name.charAt(0)}
                                    </div>
                                  </div>
                                  <div className="flex-1 text-left">
                                    <div className="text-white font-bold">{currentTeams.home.fullName}</div>
                                    <div className="text-xs text-white/60">Home Team</div>
                                  </div>
                                </div>
                              </button>
                            )}

                            {/* Away Team Button */}
                            {currentTeams.away && (
                              <button
                                onClick={() => {
                                  setUserTeamPreference('away');
                                  setShowTeamSelector(false);
                                  setTimeout(() => {
                                    const form = document.querySelector('form');
                                    if (form) form.requestSubmit();
                                  }, 100);
                                }}
                                style={{
                                  background: `linear-gradient(to right, ${teamColors.right}40, ${teamColors.right}20)`,
                                  borderColor: `${teamColors.right}80`
                                }}
                                className="w-full p-4 rounded-lg border-2 hover:brightness-110 transition-all group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden group-hover:scale-110 transition-transform">
                                    {teamStats?.b?.team_logo_url ? (
                                      <img 
                                        src={teamStats.b.team_logo_url} 
                                        alt={currentTeams.away.name}
                                        className="w-10 h-10 object-contain"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center text-lg font-bold ${teamStats?.b?.team_logo_url ? 'hidden' : 'flex'}`}>
                                      {currentTeams.away.name.charAt(0)}
                                    </div>
                                  </div>
                                  <div className="flex-1 text-left">
                                    <div className="text-white font-bold">{currentTeams.away.fullName}</div>
                                    <div className="text-xs text-white/60">Away Team</div>
                                  </div>
                                </div>
                              </button>
                            )}

                            {/* Neutral Button */}
                            <button
                              onClick={() => {
                                setUserTeamPreference('neutral');
                                setShowTeamSelector(false);
                                setTimeout(() => {
                                  const form = document.querySelector('form');
                                  if (form) form.requestSubmit();
                                }, 100);
                              }}
                              className="w-full p-4 rounded-lg bg-gradient-to-r from-gray-500/20 to-gray-600/20 border-2 border-gray-500/50 hover:border-gray-400 transition-all group"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gray-500/30 flex items-center justify-center text-lg group-hover:scale-110 transition-transform">
                                  ⚽
                                </div>
                                <div className="flex-1 text-left">
                                  <div className="text-white font-bold">Neutral Fan</div>
                                  <div className="text-xs text-white/60">Just here for the match</div>
                                </div>
                              </div>
                            </button>
                          </div>

                          <button
                            onClick={() => setShowTeamSelector(false)}
                            className="mt-4 w-full py-2 text-sm text-white/60 hover:text-white/80 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Vertical toolbar pinned near the top */}
              <div className="absolute top-14 right-2 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTab('stats')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center shadow ${tab === 'stats' ? 'bg-gradient-to-br from-cyan-500/40 to-indigo-500/40 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  title="Match Stats"
                >
                  <StatsIcon active={tab === 'stats'} />
                </button>
                <button
                  type="button"
                  onClick={() => setTab('events')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center shadow ${tab === 'events' ? 'bg-gradient-to-br from-cyan-500/40 to-indigo-500/40 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  title="Match Events"
                >
                  <EventsIcon active={tab === 'events'} />
                </button>
                <button
                  type="button"
                  onClick={() => setTab('betting')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center shadow ${tab === 'betting' ? 'bg-gradient-to-br from-cyan-500/40 to-indigo-500/40 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  title="Betting"
                >
                  <BetIcon active={tab === 'betting'} />
                </button>
                <button
                  type="button"
                  onClick={() => setTab('lineup')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center shadow ${tab === 'lineup' ? 'bg-gradient-to-br from-cyan-500/40 to-indigo-500/40 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  title="Team Lineup"
                >
                  <FontAwesomeIcon icon={faShirt} />
                </button>
                <button
                  type="button" onClick={() => setTab('chat')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center shadow ${tab === 'chat' ? 'bg-gradient-to-br from-cyan-500/40 to-indigo-500/40 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/15'}`}
                  title="Live Chat">
                  <ChatIcon active={tab === 'chat'} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
